<script setup lang="ts">
import { effectiveSingleItemShippingFee } from '~/utils/commercePolicy'
import type { ShopProduct } from '~/composables/useShopAPI'

const props = withDefaults(defineProps<{
  product: ShopProduct
  imageSize?: number
  imagePriority?: boolean
}>(), {
  imageSize: 400,
  imagePriority: false,
})

const { t } = useI18n()
const localePath = useLocalePath()
const { formatPrice, getDiscount } = useShopUtils()
const { translateTitle } = useShopI18n()
const weeklyDealIsActive = useWeeklyDealExpiry(computed(() => props.product.activeWeeklyDeal))
const serverDisplayPrice = computed(() => props.product.displayFromPrice ?? props.product.salePrice)
const displayPrice = computed(() => {
  const deal = props.product.activeWeeklyDeal
  // The catalogue response is a snapshot. Once its deal expires, never keep
  // advertising the campaign price while the fresh catalogue is loading.
  if (!weeklyDealIsActive.value && deal
    && Number(serverDisplayPrice.value) === Number(deal.campaignPrice)) {
    return deal.regularPrice
  }
  return serverDisplayPrice.value
})

watch(weeklyDealIsActive, (active, wasActive) => {
  if (wasActive && !active) {
    void refreshNuxtData()
  }
})
const cardAdvertisesWeeklyDeal = computed(() => {
  const deal = props.product.activeWeeklyDeal
  if (!weeklyDealIsActive.value || !deal) return false
  return deal.targetType !== 'INVENTORY_ITEM'
    || Number(displayPrice.value) === Number(deal.campaignPrice)
})

const productLink = computed(() => {
  const path = props.product.slug ? `/producto/${props.product.slug}` : `/producto/${props.product.id}`
  const deal = props.product.activeWeeklyDeal
  // When the exact deal is the advertised catalogue from-price, preserve that
  // promise by opening the matching device. If another ordinary device is cheaper,
  // the card remains a generic from-price card and does not show a deal badge.
  return cardAdvertisesWeeklyDeal.value && deal?.targetType === 'INVENTORY_ITEM' && deal.inventoryItemId
    ? localePath({ path, query: { inventory: deal.inventoryItemId } })
    : localePath(path)
})

const compareAtPrice = computed(() => {
  const deal = props.product.activeWeeklyDeal
  if (cardAdvertisesWeeklyDeal.value && deal && Number(displayPrice.value) === Number(deal.campaignPrice)) {
    return deal.regularPrice
  }
  return props.product.originalPrice
})
const discount = computed(() => {
  if (!compareAtPrice.value) return 0
  return getDiscount(displayPrice.value, compareAtPrice.value)
})

// Warranty messaging applies to refurbished devices; suppress it on accessories.
const isAccessory = computed(() => props.product.productType === 'ACCESSORY')
const displayPriceLabel = computed(() => {
  const price = formatPrice(displayPrice.value)
  return isAccessory.value ? price : t('productPage.conditionFrom', { price })
})

// 运费：有有效模版运费(>0)显示「运费 €X」，否则显示「免费送货」。
// shippingFee 由后端按模版启用状态算成「有效值」(停用/无模版 → null)。
const hasShippingFee = computed(() => {
  return effectiveSingleItemShippingFee(
    props.product.shippingFee,
    props.product.shippingFreeThreshold,
    displayPrice.value,
  ) > 0
})

const shippingFeeLabel = computed(() => {
  if (!hasShippingFee.value) return t('shopPage.trustBadges.shipping')
  return t('shopPage.product.shippingFee', {
    price: formatPrice(props.product.shippingFee!),
  })
})

const shippingThresholdLabel = computed(() => {
  const threshold = props.product.shippingFreeThreshold
  if (!hasShippingFee.value || threshold == null || threshold <= 0) return null
  return t('shopPage.product.shippingFreeOver', {
    threshold: formatPrice(threshold),
  })
})
</script>

<template>
  <NuxtLink
    :to="productLink"
    class="group block rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
  >
    <div class="bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all duration-200 motion-reduce:transition-none h-full flex flex-col">
      <!-- Image -->
      <div class="relative aspect-square bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
        <img
          v-if="product.images && product.images.length > 0"
          :src="imageUrl(product.images[0], { w: imageSize, h: imageSize })"
          :alt="translateTitle(product.titleTranslations, product.title)"
          :width="imageSize"
          :height="imageSize"
          :loading="imagePriority ? 'eager' : 'lazy'"
          :fetchpriority="imagePriority ? 'high' : 'auto'"
          class="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300 motion-reduce:transform-none motion-reduce:transition-none"
        >
        <UIcon v-else name="i-lucide-smartphone" aria-hidden="true" class="w-20 h-20 text-gray-300 dark:text-gray-600" />

        <!-- Discount Badge -->
        <div v-if="discount > 0" class="absolute top-3 left-3 z-10">
          <span class="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
            -{{ discount }}%
          </span>
        </div>
        <div v-if="cardAdvertisesWeeklyDeal" class="absolute right-3 top-3 z-10 rounded bg-orange-500 px-2 py-1 text-xs font-bold text-white">
          {{ t('weeklyDeal.badge') }}
        </div>
      </div>

      <!-- Info -->
      <div class="p-4 flex-grow flex flex-col">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 min-h-[2.5rem] group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
          {{ translateTitle(product.titleTranslations, product.title) }}
        </h3>

        <div class="mt-auto pt-2">
          <!-- Price -->
          <div class="flex items-baseline gap-2 mb-2">
            <span class="text-lg font-bold text-gray-900 dark:text-white">
              {{ displayPriceLabel }}
            </span>
            <span v-if="compareAtPrice" class="text-sm text-gray-600 dark:text-gray-400 line-through">
              {{ formatPrice(compareAtPrice) }}
            </span>
          </div>

          <!-- Bottom details wrap on narrow cards so shipping terms are never clipped. -->
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400 min-h-5">
            <template v-if="!isAccessory">
              <div class="flex items-center gap-1">
                <UIcon name="i-lucide-shield-check" aria-hidden="true" class="w-3 h-3 text-green-600 dark:text-green-400" />
                <span>{{ t('shopPage.product.warranty') }}</span>
              </div>
            </template>

            <div class="flex min-w-0 items-start gap-1">
              <UIcon name="i-lucide-truck" aria-hidden="true" class="mt-0.5 w-3 h-3 shrink-0 text-brand-600 dark:text-brand-400" />
              <span>
                {{ shippingFeeLabel }}
                <span v-if="shippingThresholdLabel" class="block text-gray-600 dark:text-gray-300">
                  {{ shippingThresholdLabel }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
